/* Copyright (c) 2020-present CloudIO Inc */

import { isEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSendEmail';

const ActionSendEmailManager: ActionManagerFn<'ActionSendEmail'> = (
  platform: Platform,
): ActionManager<'ActionSendEmail'> => {
  return {
    ...baseActionManager(platform, 'ActionSendEmail'),
    defaultValue,
    props: {
      ...baseActionProps,
      toUser: {
        label: 'To User',
        type: 'expression',
        helpText: 'select the user',
        returnType: async () => 'user',
        required: (item: RequiredItem<'ActionSendEmail'>) =>
          !item.props.to.value,
      },
      to: {
        label: 'To Email',
        type: 'expression',
        helpText: 'Email address of the to user',
        required: (item: RequiredItem<'ActionSendEmail'>) =>
          !item.props.toUser.value,
        returnType: async () => 'string',
      },
      subject: {
        label: 'Subject',
        type: 'expression',
        required: true,
        helpText: 'Email subject',
        returnType: async () => 'string',
      },
      body: {
        label: 'Body',
        type: 'expression',
        required: true,
        helpText: 'Email body',
        returnType: async () => 'string',
      },
      attachmentId: {
        label: 'Attachment Id',
        type: 'expression',
        helpText: 'Enter the attachement id',
        returnType: async () => 'string',
      },
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'to':
          return !props.toUser.value;
        case 'toUser':
          return !props.to.value;
        default:
          return true;
      }
    },
    onPropertyChange: (property, value, itemProps, context) => {
      if (property === 'to' && !isEmpty(value)) {
        return {
          [property]: value,
          toUser: { value: '', type: 'text' },
        };
      } else if (property === 'toUser' && !isEmpty(value)) {
        return {
          [property]: value,
          to: { value: '', type: 'text' },
        };
      }
      return {
        [property]: value,
      };
    },
    name: 'Send Email',
    icon: { iconName: 'envelope' },
    description: `**Overview**<br/>
The Send Email action is a communication component designed to dispatch electronic mail directly from the application logic. It allows developers to configure automated email notifications, reports, or alerts triggered by specific user interactions or system events.<br/><br/>
This component offers flexible addressing options, allowing messages to be sent either to a registered system user (resolving their email automatically) or to any external email address string. It also supports dynamic content generation for the subject and body, as well as the inclusion of file attachments.

**Key Features**<br/>
* **Dual Addressing Modes:** Supports targeting recipients either by their internal User object (toUser) or by a raw email string (to). The interface enforces mutual exclusivity, ensuring clear intent.
* **Smart Visibility:** The configuration panel dynamically hides the unused addressing field to keep the UI clean (e.g., if "To User" is selected, "To Email" is hidden).
* **Attachment Support:** Features a dedicated attachmentId property, enabling the action to look up a specific file stored in the system and attach it to the outgoing message.
* **Dynamic Content:** All primary fields (Subject, Body, Recipient) support expressions, allowing the email content to be personalized at runtime (e.g., "Hello #{User.firstName}").

**Properties**<br/>
* **To User:** An expression property returning a User object.
  * *Function:* Selects a registered user from the system to receive the email.
  * *Constraint:* Mutually exclusive with "To Email" (one is required).
* **To Email:** An expression property returning a string.
  * *Function:* Manually specifies the recipient's email address (e.g., 'external.vendor@example.com').
  * *Constraint:* Mutually exclusive with "To User".
* **Subject (Required):** An expression property returning a string.
  * *Function:* The subject line of the email.
* **Body (Required):** An expression property returning a string.
  * *Function:* The main content of the email message.
* **Attachment Id:** An expression property returning a string.
  * *Function:* The unique identifier of a file to be attached to the email.

**Use Cases**<br/>
* **Welcome Messages:** Automatically sending a "Welcome to the Platform" email to a user immediately after they successfully register (ActionCreateRecord).
* **Report Delivery:** Generating a PDF report via a server function and then using this action (with attachmentId) to email it to a manager.
* **External Alerts:** Notifying a third-party supplier via their support email address when inventory for a specific part drops below a threshold.`,
  };
};

export default ActionSendEmailManager;
